import { QueryClient } from '@tanstack/react-query';

import { useGetKeywordSearchMagazineCursorPageQuery } from '@/hooks/magazine/mobile/magazine.gql.impl';
import { MagazineType } from '@/types/magazine';

import { AuthorType, GetKeywordSearchMagazineCursorPageQuery } from '@/types/bff/gql-schema';
import { EAuthorType, KeywordSearchMagazineVo } from '@/types/search';
import dayjs from 'dayjs';
import useGetKeywordSearchMagazineInfiniteQuery from './useGetKeywordSearchMagazineInfiniteQuery';

interface Props {
  keyword: string;
  size?: number;
  contentType?: MagazineType;
  enabled?: boolean;
}

const MAGAZINE_SIZE = 20;

const parseAuthorType = (authorType: AuthorType): EAuthorType => {
  switch (authorType) {
    case AuthorType.Doctor:
      return EAuthorType.Doctor;
    case AuthorType.Nurse:
      return EAuthorType.Nurse;
    case AuthorType.Ai:
      return EAuthorType.Ai;
    case AuthorType.GeneralEditor:
      return EAuthorType.GeneralEditor;
    case AuthorType.MedicalProfessionalEditor:
      return EAuthorType.MedicalProfessionalEditor;
  }
};

const parseSearchResultMagazineVo = (
  data: Array<GetKeywordSearchMagazineCursorPageQuery>
): Array<KeywordSearchMagazineVo> => {
  if (data) {
    return data
      .flatMap((page) => page.keywordSearchMagazineCursorPage.elements)
      .map((magazine) => {
        return {
          ...magazine,
          createdDate: `${dayjs(magazine.createdAt).format('YYYY.MM.DD')}`,
          author: magazine.author
            ? {
                ...magazine.author,
                authorType: parseAuthorType(magazine.author.authorType),
              }
            : undefined,
        };
      });
  }
  return [] as Array<KeywordSearchMagazineVo>;
};

const useGetKeywordSearchMagazineInfinite = ({ keyword, size = MAGAZINE_SIZE, contentType, enabled = true }: Props) => {
  const {
    data: magazinesData,
    isLoading: isMagazinesLoading,
    hasNextPage: hasMagazinesNextPage,
    fetchNextPage: fetchMagazinesNextPage,
  } = useGetKeywordSearchMagazineInfiniteQuery(
    {
      request: {
        contentType,
        keyword,
        size,
      },
    },
    {
      select: (data) => {
        return {
          pages: parseSearchResultMagazineVo(data.pages),
          pageParams: data.pageParams,
        };
      },
      enabled: enabled && !!keyword && keyword.length > 0,
    }
  );

  const magazines = magazinesData?.pages.flatMap((magazine) => magazine) || [];

  return {
    magazines,
    magazinesData,
    isMagazinesLoading,
    hasMagazinesNextPage,
    fetchMagazinesNextPage,
  };
};

export default useGetKeywordSearchMagazineInfinite;

useGetKeywordSearchMagazineInfinite.prefetchQuery = async (
  queryClient: QueryClient,
  params: Omit<Props, 'enabled'>
) => {
  await queryClient.prefetchInfiniteQuery(
    useGetKeywordSearchMagazineCursorPageQuery.getKey({
      request: params,
    }),
    useGetKeywordSearchMagazineCursorPageQuery.fetcher({
      request: params,
    })
  );
};

const getNonBenefitHomeFAQParams = (keyword: string) => {
  return {
    keyword,
    size: 5,
    contentType: MagazineType.FAQ,
  };
};

const prefetchFAQ = async (queryClient: QueryClient, params: Pick<Props, 'keyword'>) => {
  await queryClient.prefetchInfiniteQuery(
    useGetKeywordSearchMagazineCursorPageQuery.getKey({
      request: {
        ...getNonBenefitHomeFAQParams(params.keyword),
      },
    }),
    useGetKeywordSearchMagazineCursorPageQuery.fetcher({
      request: {
        ...getNonBenefitHomeFAQParams(params.keyword),
      },
    })
  );
};

useGetKeywordSearchMagazineInfinite.getNonBenefitHomeFAQParams = getNonBenefitHomeFAQParams;
useGetKeywordSearchMagazineInfinite.prefetchFAQ = prefetchFAQ;

const getNonBenefitHomeArticleParams = (keyword: string) => {
  return {
    keyword,
    size: 5,
    contentType: MagazineType.ARTICLE,
  };
};

const prefetchArticle = async (queryClient: QueryClient, params: Omit<Props, 'enabled'>) => {
  await queryClient.prefetchInfiniteQuery(
    useGetKeywordSearchMagazineCursorPageQuery.getKey({
      request: {
        ...getNonBenefitHomeArticleParams(params.keyword),
      },
    }),
    useGetKeywordSearchMagazineCursorPageQuery.fetcher({
      request: {
        ...getNonBenefitHomeArticleParams(params.keyword),
      },
    })
  );
};

useGetKeywordSearchMagazineInfinite.getNonBenefitHomeArticleParams = getNonBenefitHomeArticleParams;
useGetKeywordSearchMagazineInfinite.prefetchArticle = prefetchArticle;
